'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiListSubheader, {
  type ListSubheaderTypeMap as MuiListSubheaderTypeMap,
} from '@mui/material/ListSubheader';
import { useTheme } from '@mui/material/styles';

declare module '@mui/material/ListSubheader' {
  interface ListSubheaderPropsColorOverrides {
    forceWhite: true;
  }
}

type ExcludedMuiProps = 'color';

type CustomProps = {
  color?: 'default' | 'forceWhite' | 'inherit' | 'primary' | undefined;
  component?: ElementType;
};

type ListSubheaderTypeMap<
  P = CustomProps,
  D extends ElementType = MuiListSubheaderTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiListSubheaderTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `List` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-list-migration--list-migration-guide Migration guide}
 */
export type ListSubheaderProps<
  D extends ElementType = MuiListSubheaderTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<ListSubheaderTypeMap<P, D>, D>;

const ListSubheaderComponent = (
  { color, sx = [], ...props }: ListSubheaderProps,
  ref: Ref<HTMLLIElement>
) => {
  const theme = useTheme();
  const resolvedColor =
    color === 'forceWhite'
      ? {
          backgroundColor: 'transparent',
          color: theme.palette.forceWhite.main,
        }
      : { color: color };
  return (
    <MuiListSubheader
      {...props}
      ref={ref}
      sx={[resolvedColor, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};

ListSubheaderComponent.displayName = 'ListSubheader';

/**
 * @deprecated Please use `List` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-list-migration--list-migration-guide Migration guide}
 */
export const ListSubheader = forwardRef(
  ListSubheaderComponent
) as OverridableComponent<ListSubheaderTypeMap>;
